/**
 * legal — the resolved legal-document override every reader (checkout
 * disclosures, the public /legal page, the staff consent screen) reads
 * through: `content` seeded synchronously at module evaluation to the
 * "nothing published" shape, so `resolveLegalDocument` (@momo/i18n)
 * immediately falls back to the platform default and no consumer ever
 * observes a pending state — the legal page is public and its availability
 * cannot depend on JS + App Check + reCAPTCHA + Firestore all succeeding.
 * `settled` distinguishes "the override has been checked at least once"
 * from "still checking" for the one consumer that needs a stable read
 * before it lets the user act on it (sign-consent.view.ts, see there);
 * every other consumer ignores it, since `content` is already a valid
 * resolvable value the instant this module evaluates. legal.sync.ts
 * (web-side) is the sole writer, folding in the content-addressed override
 * it resolves off the workspace card's own `legalContent` pointer when it
 * arrives; a slow resolve leaves the cell unsettled until it does.
 *
 * `legalIdentityFrom`, `isLegalContentPublished` and `legalDocumentFor` are
 * shared by every consumer that resolves a document for display —
 * deduplicated here rather than duplicated per view, since @momo/i18n
 * cannot import `Workspace` itself (packages/types already depends on
 * @momo/i18n; the other direction would cycle).
 *
 * `legalAnchorHref`/`isPlainLeftClick` are the other duplication this file
 * ends: every in-app document link (checkout-disclosures, sign-consent,
 * legal-footer) renders a real `/legal#<anchor>` href and intercepts only
 * an unmodified left click to open the overlay in place — a modified click
 * (⌘/ctrl/shift/a non-primary button) falls through to the href's own
 * genuine navigation, so "open in new tab" keeps working. One shared
 * definition of "which click" and "which URL" instead of the same
 * modifier-key list copy-pasted three times and drifting.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type {
	DeploymentCapabilities,
	LegalContent,
	LegalDocument,
	LegalDocumentKind,
	LegalIdentity,
	Locale,
} from "@momo/i18n";
import { resolveLegalDocument } from "@momo/i18n";
import type { Workspace } from "@momo/types";
import { Subject } from "rxjs";
import { organizationPathPrefix } from "../workspace/organization.state.ts";
import {
	workspaceCard,
	workspaceMapsApiKey,
} from "../workspace/workspace.state.ts";

/** The "nothing published" shape — every field absent, so
 *  `resolveLegalDocument` falls back to the platform default for both
 *  kinds and both locales. Also this module's own seed value. */
export const emptyLegalContent: LegalContent = {
	terms: { version: null, de: null, en: null },
	privacy: { version: null, de: null, en: null },
	publishedAt: null,
};

export type LegalContentState = {
	/** True once the override has answered — its text, the default where
	 *  none is published, or `failed` (legal.sync.ts, web-side). False while
	 *  the read is still on its way, however long that takes. */
	readonly settled: boolean;
	readonly content: LegalContent;
	/** The organization's card could not be read, so whether it overrides the
	 *  default is unknown: nothing may be consented to until it is. */
	readonly failed?: boolean;
	/** Present only on the bare host, where no organization is open and a
	 *  founder contracts with the deployment's operator: its identity from
	 *  `platform/legal`, or `"absent"` when no such record exists, so nobody
	 *  can be contracted with and sign-up is refused. An organization's
	 *  storefront carries none; its identity is its card's. */
	readonly operator?: Workspace.OperatorIdentity | "absent";
};

const initial: LegalContentState = {
	settled: false,
	content: emptyLegalContent,
};

// keepOnClear: public, identity-free — mirrors workspaceCard's own reasoning
// (workspace/workspace.state.ts): only a live Firestore listener writes this,
// so clearStates() wiping it to `initial` would strand it there forever since
// nothing re-writes an unchanged doc across a sign-out/sign-in.
export const legalContentState = state<LegalContentState>(
	"legal/content",
).memory(initial, { keepOnClear: true });

/** The deployment operator's own platform terms and privacy notice
 *  (`platform/legal`), read while an organization is open — what the owner
 *  invitation a staff approval founded an organization with is accepted
 *  under, since accepting it concludes the founder's contract with the
 *  operator rather than with the organization. The same shape the bare host's
 *  `legalContentState` carries, `operator` always set; unsettled while no
 *  founding invitation is on screen. One writer: legal.sync.ts's
 *  mountPlatformLegal (@momo/session). */
export const platformLegalState = state<LegalContentState>(
	"legal/platform",
).memory(initial, { keepOnClear: true });

/** A request to read the organization's text again after it failed —
 *  legal.sync.ts (web-side) re-resolves the current organization, and the
 *  operator's text where it is read, on it. */
export const legalRetry$ = new Subject<void>();

/** The identity the § 5 DDG imprint renders: on the bare host the
 *  deployment operator's record, elsewhere the organization's card. Null when
 *  that block is missing or carries no contact channel — a present card
 *  block may predate `contactEmail`, and a withheld statutory disclosure
 *  beats a fabricated one, per `Workspace.legal`'s own rule. The imprint
 *  section and every link to it read this, never the card directly. */
export const imprintIdentity = computed(() => {
	const operator = legalContentState.value.operator;
	const legal =
		operator === undefined
			? (workspaceCard.value.workspace?.legal ?? null)
			: operator === "absent"
				? null
				: operator;
	return legal?.contactEmail != null ? legal : null;
});

/** Whether there is a lawful imprint to show (`imprintIdentity`). */
export const imprintAvailable = computed(() => imprintIdentity.get() !== null);

/** Whether `kind` is fully published in `content` — its version and BOTH
 *  locale texts present. Mirrors `resolveLegalDocument`'s own gate
 *  (packages/i18n/src/legal.ts) so a consumer that also needs to know
 *  *which* branch resolved (to decide `identityUnavailable` below) shares
 *  one answer rather than re-deriving it. */
export function isLegalContentPublished(
	content: LegalContent,
	kind: LegalDocumentKind,
): boolean {
	const doc = content[kind];
	return doc.version != null && doc.de != null && doc.en != null;
}

/** The `LegalIdentity` a platform-default document interpolates, assembled
 *  from the public workspace card with `unavailable` standing in
 *  field-by-field for whatever hasn't been configured yet — never
 *  fabricated, per `Workspace.legal`'s own rule. Irrelevant once `kind` is
 *  published (`isLegalContentPublished`): an override's source text
 *  carries no identity placeholders to fill. */
export function legalIdentityFrom(
	workspace: Workspace.Workspace | null,
	domain: string,
	unavailable: string,
): LegalIdentity {
	const legal = workspace?.legal ?? null;
	return {
		brandName: workspace?.brand.name ?? unavailable,
		legalName: legal?.legalName ?? unavailable,
		address: legal?.address ?? unavailable,
		vatId: legal?.vatId ?? unavailable,
		register: legal?.register ?? unavailable,
		representative: legal?.representative ?? unavailable,
		contactEmail: legal?.contactEmail ?? unavailable,
		domain,
	};
}

/** This deployment's live capability set, off the public workspace card
 *  plus `workspaceMapsApiKey.get()` — every switch the legal builders gate a
 *  capability-scoped disclosure on.
 *
 *  `Workspace.wallet.{googleEnabled,appleEnabled}` is the public mirror of
 *  `WorkspaceSettings.wallet` a Firestore trigger backfills, so an anonymous
 *  buyer's session can read it even though the private settings doc it
 *  mirrors is member-gated — the same card read every consumer here already
 *  holds. `?? false` covers both a genuinely disabled wallet and a workspace
 *  whose mirror trigger hasn't run yet: absence reads as "not enabled", never
 *  as a reason to guess. `ads.meta.pixelId` and `ads.meta.browserTag` ride
 *  the same card: the id is written by the server once the Meta access token
 *  has proved it can read that pixel, the flag is the operator's own second
 *  decision on top of it, and `metaAdsTier` (@momo/i18n) is what turns the
 *  pair into the one fact both the policy's Meta disclosure and
 *  `<checkout-disclosures>`'s own ad-report cue read. The flag reads
 *  `?? false` for the same reason the wallet booleans do — and because the
 *  two Meta tiers disclose materially different things, so guessing the tag
 *  on would print a cookie that is never set.
 *
 *  Read fresh at call time; pure, no I/O. Reading it inside a `render()`
 *  tracks the card, so a view gating on it re-renders when the card lands. */
export function deploymentCapabilities(): DeploymentCapabilities {
	const workspace = workspaceCard.value.workspace;
	return {
		walletGoogleEnabled: workspace?.wallet?.googleEnabled ?? false,
		walletAppleEnabled: workspace?.wallet?.appleEnabled ?? false,
		mapsApiKey: workspaceMapsApiKey.get() ?? null,
		metaPixelId: workspace?.ads?.meta?.pixelId ?? null,
		metaBrowserTag: workspace?.ads?.meta?.browserTag ?? false,
	};
}

/** No capability switched on — what the operator's own documents resolve
 *  with, since they describe the platform account rather than any
 *  organization's buyer-facing features. */
const noCapabilities: DeploymentCapabilities = {
	walletGoogleEnabled: false,
	walletAppleEnabled: false,
	mapsApiKey: null,
	metaPixelId: null,
	metaBrowserTag: false,
};

/** The resolved `LegalDocument` plus its identity-availability flag for
 *  `kind` — the exact workspaceCard → legalIdentityFrom → legalContentState
 *  → resolveLegalDocument chain checkout-disclosures.view.ts,
 *  sign-consent.view.ts and legal-footer.view.ts each need before opening
 *  `kind` in the shared overlay. Reads workspaceCard/legalContentState
 *  fresh at call time; pure, no I/O.
 *
 *  `capabilities` is assembled by `deploymentCapabilities` above rather
 *  than threaded in by every caller. `resolveLegalDocument` ignores it
 *  entirely for `kind === "terms"`, so passing it unconditionally is
 *  harmless there. The operator's documents describe the platform account,
 *  never an organization's buyer-facing capabilities, so they resolve with
 *  none switched on, whichever organization is open.
 *
 *  `legal` is the cell resolved from — the open scope's own by default, or
 *  `platformLegalState` for the operator's texts on an organization. */
export function legalDocumentFor(
	kind: LegalDocumentKind,
	locale: Locale,
	domain: string,
	unavailable: string,
	legal: LegalContentState = legalContentState.value,
): { document: LegalDocument | null; identityUnavailable: boolean } {
	const { content, failed, operator } = legal;
	const workspace = workspaceCard.value.workspace;
	// On the bare host the operator is the party, and its brand and domain are
	// the host's: the record states neither. With no operator there is no
	// identity at all, not one made of placeholders.
	const identity: LegalIdentity | null =
		operator === undefined
			? legalIdentityFrom(workspace, domain, unavailable)
			: operator === "absent"
				? null
				: {
						brandName: domain,
						legalName: operator.legalName,
						address: operator.address,
						vatId: operator.vatId,
						register: operator.register,
						representative: operator.representative,
						contactEmail: operator.contactEmail ?? unavailable,
						domain,
					};
	const party = operator ?? workspace?.legal ?? null;
	return {
		// The text could not be read, or there is no operator to contract with:
		// no document stands in, the platform default least of all.
		document:
			failed || identity === null
				? null
				: resolveLegalDocument(
						kind,
						locale,
						content,
						identity,
						operator === undefined ? deploymentCapabilities() : noCapabilities,
					),
		identityUnavailable:
			!isLegalContentPublished(content, kind) &&
			(party === null || party === "absent"),
	};
}

/** The three section ids legal-page.view.ts's own DOM carries (`#terms`,
 *  `#privacy`, `#imprint`) — the closed set of destinations an in-app legal
 *  link may open, either as this overlay or as a genuine `/legal#<anchor>`
 *  navigation. Widens `LegalDocumentKind` (@momo/i18n) by `"imprint"`,
 *  which isn't a resolved `LegalDocument` at all — it renders straight off
 *  `Workspace.legal`. */
export type LegalAnchor = LegalDocumentKind | "imprint";

/** The real, resolvable, shareable `/legal#<anchor>` URL for `anchor` —
 *  every in-app document link renders this as its `href` rather than
 *  `href="#"`, so the link is crawlable and shareable, and a modified
 *  click (see `isPlainLeftClick`) still opens a genuine navigation instead
 *  of doing nothing. */
export function legalAnchorHref(anchor: LegalAnchor): string {
	return `${organizationPathPrefix.get()}/legal#${anchor}`;
}

/** Whether `e` is an unmodified primary-button left click — the only click
 *  an in-app legal link intercepts to open the overlay in place. ⌘, ctrl,
 *  shift, or any non-primary button (including the middle button) reads
 *  false here, so the caller lets the click fall through to the real
 *  `/legal#<anchor>` navigation `legalAnchorHref` already put in the
 *  `href` — "open in new tab" keeps working. */
export function isPlainLeftClick(e: MouseEvent): boolean {
	return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey;
}
